import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import "./FoodAllowance.css";
import { toast } from "react-toastify";

const FoodAllowanceForm = ({
  selectedCompany,
  companyId,
  foodallowance,
  onClose,
}) => {
  // Get the current user data (for createdBy if needed)
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const currentUserId = authData?._id;

  // State for the list of employees and vessels
  const [employees, setEmployees] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vesselCode, setVesselCode] = useState("");

  // Fetch employees for the dropdown (by companyId)
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get(
          `/foodallowance/employee/${companyId}`,
        );
        setEmployees(response.data.data);
      } catch (error) {
        toast.error("Error fetching employees");
      }
    };
    fetchEmployees();
  }, [companyId]);

  // Fetch vessels for vessel selection
  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const response = await axiosInstance.get(
          `/foodallowance/vessal/${companyId}`,
        );
        setVessels(response.data.vessels);
      } catch (error) {
        toast.error("Error fetching vessel data");
      }
    };
    fetchVessels();
  }, [companyId]);

  // Set up initial form data. Include temporary fields for employee and vessel search/dropdown.
  const initialFormData = foodallowance
    ? {
        employeeId: foodallowance.employeeId || "",
        employeeSearch: foodallowance.employee
          ? foodallowance.employee.name
          : "",
        showEmployeeDropdown: false,
        vesselSearch: "",
        showVesselDropdown: false,
        // If editing, assume the first foodDetails record is being edited.
        foodDetails:
          foodallowance.foodDetails && foodallowance.foodDetails.length > 0
            ? {
                isEaten: foodallowance.foodDetails[0].isEaten || false,
                dish: foodallowance.foodDetails[0].dish || "",
                quantity: foodallowance.foodDetails[0].quantity || "",
                hours: foodallowance.foodDetails[0].hours || "",
                date: foodallowance.foodDetails[0].date
                  ? new Date(foodallowance.foodDetails[0].date)
                      .toISOString()
                      .split("T")[0]
                  : new Date().toISOString().split("T")[0],
                vessalId: foodallowance.foodDetails[0].vessalId || "",
                amount: foodallowance.foodDetails[0].amount || "",
                allowance: foodallowance.foodDetails[0].allowance || "",
                remark: foodallowance.foodDetails[0].remark || "",
              }
            : {
                isEaten: false,
                dish: "",
                quantity: "",
                hours: "",
                date: new Date().toISOString().split("T")[0],
                vessalId: "",
                amount: "",
                allowance: "",
                remark: "",
              },
      }
    : {
        employeeId: "",
        employeeSearch: "",
        showEmployeeDropdown: false,
        vesselSearch: "",
        showVesselDropdown: false,
        foodDetails: {
          isEaten: false,
          dish: "",
          quantity: "",
          hours: "",
          date: new Date().toISOString().split("T")[0],
          vessalId: "",
          amount: "",
          allowance: "",
          remark: "",
        },
      };

  const [formData, setFormData] = useState(initialFormData);

  // Compute the selected employee (if one is chosen)
  const selectedEmployee = employees.find(
    (emp) => emp._id === formData.employeeId,
  );

  // Compute the selected vessel based on foodDetails.vessalId
  const selectedVessel = vessels.find(
    (v) => v._id === formData.foodDetails.vessalId,
  );

  // Remove temporary properties before submitting
  const sanitizeFormData = (data) => {
    const sanitized = { ...data };
    delete sanitized.employeeSearch;
    delete sanitized.showEmployeeDropdown;
    delete sanitized.vesselSearch;
    delete sanitized.showVesselDropdown;
    return sanitized;
  };

  // Close modal if clicking outside the dialog
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Update form state. Supports both top-level fields and nested foodDetails fields.
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("foodDetails.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        foodDetails: {
          ...prev.foodDetails,
          [field]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // On submit, send the payload to the addFood endpoint.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employeeId) {
      toast.error("Please select an employee.");
      return;
    }
    setLoading(true);
    const payload = sanitizeFormData(formData);
    try {
      // The backend addFood endpoint expects employeeId and foodDetails
      const response = await axiosInstance.post("/foodallowance", payload);
      toast.success("Food detail added successfully");
      // console.log("Response:", response);
      onClose();
    } catch (error) {
      // console.error("Error response:", error.response);
      let errorMessage = "Error saving food detail";
      if (error.response?.data?.message) {
        errorMessage = Array.isArray(error.response.data.message)
          ? error.response.data.message.join(", ")
          : error.response.data.message;
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content foodallowance_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">
              {foodallowance ? "Edit Food Detail" : "Add Food Detail"}
            </h5>
            <button
              type="button"
              className="btn-close text-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body vesel_form">
            <form onSubmit={handleSubmit}>
              {/* Employee Selection */}
              <div className="row mb-3">
                <div className="col-md-6 mb-3  position-relative">
                  <label className="form-label">Employee</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type employee name or code"
                    name="employeeSearch"
                    value={formData.employeeSearch}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        employeeSearch: e.target.value,
                        employeeId: "",
                      }))
                    }
                    onFocus={() =>
                      setFormData((prev) => ({
                        ...prev,
                        showEmployeeDropdown: true,
                      }))
                    }
                    onBlur={() => {
                      setTimeout(() => {
                        setFormData((prev) => ({
                          ...prev,
                          showEmployeeDropdown: false,
                        }));
                      }, 150);
                    }}
                    required
                  />
                  {formData.showEmployeeDropdown && (
                    <ul
                      className="list-group position-absolute"
                      style={{
                        zIndex: 1200,
                        width: "100%",
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {employees
                        .filter(
                          (item) =>
                            (item.name || "")
                              .toLowerCase()
                              .includes(
                                formData.employeeSearch.toLowerCase(),
                              ) ||
                            (item.employeeNo
                              ? item.employeeNo.toString()
                              : ""
                            ).includes(formData.employeeSearch.toLowerCase()),
                        )
                        .map((item) => (
                          <li
                            key={item._id}
                            className="list-group-item list-group-item-action"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                employeeId: item._id,
                                employeeSearch: item.name,
                                showEmployeeDropdown: false,
                              }))
                            }
                          >
                            {`${item.name} - ${item.employeeNo}`}
                          </li>
                        ))}
                      {employees.filter(
                        (item) =>
                          (item.name || "")
                            .toLowerCase()
                            .includes(formData.employeeSearch.toLowerCase()) ||
                          (item.employeeNo
                            ? item.employeeNo.toString()
                            : ""
                          ).includes(formData.employeeSearch.toLowerCase()),
                      ).length === 0 && (
                        <li className="list-group-item list-group-item-action">
                          No results found
                        </li>
                      )}
                    </ul>
                  )}
                  {/* Display selected employee details */}
                  {selectedEmployee && (
                    <div className="selected-employee mt-2">
                      <strong>Selected Employee:</strong>{" "}
                      {selectedEmployee.name} - {selectedEmployee.employeeNo}
                    </div>
                  )}
                </div>
              </div>

              {/* Food Details Section */}
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Food Details</h5>
                  <div className="row mb-3">
                    {/* Food Date */}
                    <div className="col-md-3">
                      <label className="form-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="foodDetails.date"
                        value={formData.foodDetails.date}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    {/* Vessel selection */}
                    <div className="col-md-4 mb-3  position-relative">
                      <label className="form-label">Vessel</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type vessel name or code"
                        value={
                          vessels.find(
                            (item) =>
                              item._id === formData.foodDetails.vessalId,
                          )?.vessel_name ||
                          formData.vesselSearch ||
                          ""
                        }
                        onChange={(e) => {
                          const searchValue = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            vesselSearch: searchValue,
                            // Reset vessalId if search term changes
                            foodDetails: { ...prev.foodDetails, vessalId: "" },
                          }));
                        }}
                        onFocus={() =>
                          setFormData((prev) => ({
                            ...prev,
                            showVesselDropdown: true,
                          }))
                        }
                        required
                      />
                      {formData.showVesselDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 220,
                            overflowY: "auto",
                          }}
                        >
                          {vessels
                            .filter(
                              (item) =>
                                item.vessel_name
                                  .toLowerCase()
                                  .includes(
                                    formData.vesselSearch?.toLowerCase() || "",
                                  ) ||
                                item.vessel_code
                                  .toLowerCase()
                                  .includes(
                                    formData.vesselSearch?.toLowerCase() || "",
                                  ),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    foodDetails: {
                                      ...prev.foodDetails,
                                      vessalId: item._id,
                                    },
                                    vesselSearch: "",
                                    showVesselDropdown: false,
                                  }));
                                  setVesselCode(item.vessel_code);
                                }}
                              >
                                {`${item.vessel_name} - ${item.vessel_code}`}
                              </li>
                            ))}
                          {vessels.filter(
                            (item) =>
                              item.vessel_name
                                .toLowerCase()
                                .includes(
                                  formData.vesselSearch?.toLowerCase() || "",
                                ) ||
                              item.vessel_code
                                .toLowerCase()
                                .includes(
                                  formData.vesselSearch?.toLowerCase() || "",
                                ),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                      {selectedVessel && (
                        <div className="selected-vessel mt-2">
                          <strong>Selected Vessel:</strong>{" "}
                          {selectedVessel.vessel_name} -{" "}
                          {selectedVessel.vessel_code}
                        </div>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Hours</label>
                      <input
                        type="number"
                        className="form-control"
                        name="foodDetails.hours"
                        value={formData.foodDetails.hours}
                        onChange={handleChange}
                        required={formData.foodDetails.isEaten}
                        min="0"
                        step="any"
                      />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label">Allowance</label>
                      <input
                        type="number"
                        className="form-control"
                        name="foodDetails.allowance"
                        value={formData.foodDetails.allowance}
                        onChange={handleChange}
                        required={formData.foodDetails.isEaten}
                        min="0"
                        step="any"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Remark</label>
                      <textarea
                        type="text"
                        className="form-control"
                        name="foodDetails.remark"
                        value={formData.foodDetails.remark}
                        onChange={handleChange}
                        required={formData.foodDetails.isEaten}
                      />
                    </div>
                    <div className="col-md-3 d-flex align-items-center">
                      <label className="form-label  mb-5 me-2">Is Eaten?</label>
                      <label className="switch">
                        <input
                          type="checkbox"
                          name="foodDetails.isEaten"
                          checked={formData.foodDetails.isEaten}
                          onChange={handleChange}
                        />
                        <span className="slider round"></span>
                      </label>
                    </div>
                  </div>
                  {formData.foodDetails.isEaten && (
                    <>
                      <div className="row mb-3">
                        <div className="col-md-4">
                          <label className="form-label">Dish</label>
                          <input
                            type="text"
                            className="form-control"
                            name="foodDetails.dish"
                            value={formData.foodDetails.dish}
                            onChange={handleChange}
                            required={formData.foodDetails.isEaten}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            name="foodDetails.quantity"
                            value={formData.foodDetails.quantity}
                            onChange={handleChange}
                            required={formData.foodDetails.isEaten}
                            min="0"
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            name="foodDetails.amount"
                            value={formData.foodDetails.amount}
                            onChange={handleChange}
                            required={formData.foodDetails.isEaten}
                            min="0"
                            step="any"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading
                    ? foodallowance
                      ? "Updating..."
                      : "Creating..."
                    : foodallowance
                      ? "Update Food Detail"
                      : "Add Food Detail"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodAllowanceForm;
